import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { DashboardLanguage, DashboardLanguagePreference } from "../types";
import {
  DASHBOARD_LANGUAGE_CHANGE_EVENT,
  persistChosenDashboardLanguage,
  readDashboardLanguagePreference,
} from "../utils/dashboardLanguage";

interface DashboardLanguageContextValue {
  language: DashboardLanguage;
  preference: DashboardLanguagePreference;
  setPreference: (preference: DashboardLanguagePreference) => void;
}

const DashboardLanguageContext = createContext<DashboardLanguageContextValue | null>(null);

export function DashboardLanguageProvider({ children }: { children: ReactNode }) {
  // Read only: a detected language is not stored as the user's choice, so a browser language
  // the dashboard learns to support later is still picked up.
  const [preference, setPreferenceState] = useState<DashboardLanguagePreference>(() =>
    readDashboardLanguagePreference(),
  );
  const language: DashboardLanguage = preference;

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const applyPreference = useCallback((nextPreference: DashboardLanguagePreference) => {
    setPreferenceState(nextPreference);
    persistChosenDashboardLanguage(nextPreference);
    window.dispatchEvent(
      new CustomEvent(DASHBOARD_LANGUAGE_CHANGE_EVENT, {
        detail: { language: nextPreference, preference: nextPreference },
      }),
    );
  }, []);

  const value = useMemo(
    () => ({
      language,
      preference,
      setPreference: applyPreference,
    }),
    [applyPreference, language, preference],
  );

  return (
    <DashboardLanguageContext.Provider value={value}>{children}</DashboardLanguageContext.Provider>
  );
}

/**
 * Current language for shared leaf components that also render outside the
 * provider (isolated component tests); falls back to the stored preference.
 */
export function useDashboardLanguageOrStored(): DashboardLanguage {
  return useContext(DashboardLanguageContext)?.language ?? readDashboardLanguagePreference();
}

export function useDashboardLanguage(): DashboardLanguageContextValue {
  const context = useContext(DashboardLanguageContext);
  if (context === null) {
    throw new Error("useDashboardLanguage must be used within a DashboardLanguageProvider");
  }
  return context;
}
